import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment";
import AppCollapse from "components/ui/AppCollapse";
import { UTILS } from "utils";
import { CONSTANTS as C } from "config";
import PaymentInfo from "features/order-payment-info/Index";
import TableItemsDetail from "./TableItemsDetail";
import RefundOrderInfo from "./../modals/stripeRefund/RefundOrderInfo";

const OrderDetailPreview = ({
  order,
  isUserBlocked = false,
  isCheckingBlockStatus = false,
  paymentGatewayInfoModal = false,
  setBlockOrderPlacerModalVisible = () => {},
  setPaymentGatewayInfoModal = () => {},
  getOrderSourceLabel = () => {},
  getDeliveryAddress = () => {},
  getOrderTypeName = () => {},
  getGoogleMapLink = () => {},
  getPaymentLabel = () => {},
  getOrderTakenByLabel = () => {},
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);

  return (
    <>
      <AppCollapse
        title={t("Customer Information")}
        border={false}
        defaultOpen={true}
      >
        <div className="relative grid grid-cols-2 gap-y-6">
          {order?.userInfo?.name ? (
            <>
              <div className="text-14px font-medium text-gray-700">
                {t("Customer Name")}
              </div>
              <div className="text-14px text-right text-gray-500">
                <span className="inline-flex flex-wrap items-center justify-end gap-2">
                  <span>{order.userInfo.name}</span>
                  {order?.userInfo?.phoneNumber ? (
                    <button
                      type="button"
                      className={`hidden-print rounded px-2 py-0.5 text-12px text-white ${
                        isUserBlocked ? "bg-red-600" : "bg-gray-600"
                      }`}
                      onClick={() => {
                        if (!isUserBlocked) {
                          setBlockOrderPlacerModalVisible(true);
                        }
                      }}
                      disabled={isCheckingBlockStatus}
                    >
                      {isCheckingBlockStatus
                        ? t("Checking...")
                        : isUserBlocked
                        ? t("Blocked")
                        : t("Block")}
                    </button>
                  ) : null}
                </span>
              </div>
            </>
          ) : null}
          {order?.userInfo?.phoneNumber ? (
            <>
              <div className="text-14px font-medium text-gray-700">
                {t("Whatsapp Number")}
              </div>
              <div
                className="cursor-pointer text-14px text-right text-gray-500"
                dir="ltr"
                onClick={() => UTILS.openWhatsApp(order.userInfo.phoneNumber)}
                role="presentation"
              >
                <span className="text-blue-600 underline hover:text-blue-800">
                  {order.userInfo.phoneNumber}
                </span>
              </div>
            </>
          ) : null}
        </div>
      </AppCollapse>

      <AppCollapse
        title={t("Order details")}
        border={false}
        defaultOpen={true}
      >
        <div className="relative grid grid-cols-2 gap-y-6">
          <div className="text-14px font-medium text-gray-700">
            {t("Preparation Type")}
          </div>
          <div className="text-14px text-right font-medium text-red-600">
            {order.isPreOrder ? t("PRE ORDER") : t("ASAP")}
          </div>
          <div className="text-14px font-medium text-gray-700">
            {t("Order Type")}
          </div>
          <div className="text-14px text-right font-medium text-red-600">
            {getOrderTypeName()}
          </div>
          <div className="text-14px font-medium text-gray-700">
            {t("Store")}
          </div>
          <div className="text-14px text-right text-gray-500">
            {order.storeInfo?.baseLanguage?.name || "..."}
          </div>
          <div className="time__hide text-14px font-medium text-gray-700">
            {t("Order Placed Time")}
          </div>
          <div className="time__hide text-14px text-right text-gray-500">
            {order.orderPlacedTime
              ? moment.unix(order.orderPlacedTime).format("DD-MM-Y hh:mm A")
              : "..."}
          </div>
          <div className="time__hide text-14px font-medium text-gray-700">
            {t("Order Id")}
          </div>
          <div className="time__hide text-14px text-right text-gray-500">
            {order?.orderNumber ?? "..."}
          </div>
          <div className="text-14px font-medium text-gray-700">
            {t("Order From")}
          </div>
          <div className="text-14px text-right font-medium uppercase text-red-600">
            {getOrderSourceLabel()}
          </div>
          <div className="text-14px font-medium text-gray-700">
            {t("Payment")}
          </div>
          <div className="text-14px text-right text-gray-500">
            <PaymentInfo
              orderInfo={order}
              {...{ paymentGatewayInfoModal, setPaymentGatewayInfoModal }}
            >
              <span
                className={
                  order?.paymentType === C.PAYMENT_TYPE_ONLINE &&
                  UTILS.getORderPaymentGateway(order) === C.PAYMENT_GATWAYS.STRIPE
                    ? "cursor-pointer text-blue-600 underline hover:text-blue-800"
                    : ""
                }
              >
                {order.isSplitted
                  ? "Multiple Payments"
                  : order.selectedPaymentMethod
                  ? currentLanguage === "EN"
                    ? order.selectedPaymentMethod.EN.name
                    : order.selectedPaymentMethod.AR.name
                  : "..."}
              </span>
            </PaymentInfo>
          </div>
          {order?.isSplitted ? (
            <>
              <div className="text-14px font-medium text-gray-700">
                {t("Bill Splitted")}
              </div>
              <div className="text-14px text-right text-gray-500">Yes</div>
            </>
          ) : null}
          {order?.isPreOrder ? (
            <>
              <div className="text-14px font-medium text-gray-700">
                {order?.orderType?.type === C.SERVICE_TYPES.DELIVERY
                  ? t("Pre Order Delivery Slot")
                  : t("Pre Order Pickup Slot")}
              </div>
              <div className="text-14px text-right font-bold text-gray-700">
                <div>
                  {t("Date")}:{" "}
                  {moment
                    .unix(order?.preOrder?.preOrderSlotStartTime)
                    .format("DD-MM-YYYY")}
                </div>
                <div>
                  {t("Slot")}:{" "}
                  {moment
                    .unix(order?.preOrder?.preOrderSlotStartTime)
                    .format("hh:mm A")}{" "}
                  -{" "}
                  {moment
                    .unix(order?.preOrder?.preOrderSlotEndTime)
                    .format("hh:mm A")}
                </div>
              </div>
            </>
          ) : null}
          {order?.discountApplied ? (
            <>
              <div className="text-14px font-medium text-gray-700">
                {t("Discount Info")}
              </div>
              <div className="text-14px text-right text-gray-500">
                {t("Type")} ({t(order.discountApplied.discountType)}) -{" "}
                {t("Code")} ({order.discountApplied.discountCode})
              </div>
            </>
          ) : null}
        </div>
      </AppCollapse>

      {order?.orderType?.type === C.SERVICE_TYPES.DINEIN ? (
        <AppCollapse
          title={t("Dine-in")}
          border={false}
        >
          <div className="relative grid grid-cols-2 gap-y-6">
            <div className="text-14px font-medium text-gray-700">
              {t("Selected Table")}
            </div>
            <div
              className="text-14px text-right font-medium text-red-600"
              dir="ltr"
            >
              {order?.dineIn?.tableNumber || "...."}
            </div>
          </div>
        </AppCollapse>
      ) : null}

      {order?.orderType?.type === C.SERVICE_TYPES.PICKUP &&
      order?.pickup?.vehicleInfo ? (
        <AppCollapse
          title={t("Pickup")}
          border={false}
        >
          <div className="relative grid grid-cols-2 gap-y-6">
            <div className="text-14px font-medium text-gray-700">
              {t("Vehicle Info")}
            </div>
            <div
              className="text-14px text-right font-medium text-red-600"
              dir="ltr"
            >
              {order.pickup.vehicleInfo}
            </div>
          </div>
        </AppCollapse>
      ) : null}

      {order?.orderType?.type === "DELIVERY" ? (
        <AppCollapse
          title={t("Delivery Information")}
          border={false}
        >
          <div className="relative grid grid-cols-2 gap-y-6">
            {order?.deliveryAddress?.street ? (
              <>
                <div className="text-14px font-medium text-gray-700">
                  {t("Street Address")}
                </div>
                <div
                  className="cursor-pointer text-14px text-right text-blue-600"
                  onClick={() =>
                    window.open(getGoogleMapLink(), "_blank")?.focus()
                  }
                  role="presentation"
                >
                  {order.deliveryAddress.street}
                </div>
              </>
            ) : null}
            <div className="text-14px font-medium text-gray-700">
              {t("Full Address")}
            </div>
            <div className="text-14px text-right text-gray-500 break-words">
              {getDeliveryAddress()}
            </div>
            {order?.userInfo?.lifetimeSpending ? (
              <>
                <div className="text-14px font-medium text-gray-700">
                  {t("Lifetime Spending")}
                </div>
                <div className="text-14px text-right text-blue-600">
                  AED {parseFloat(order.userInfo.lifetimeSpending).toFixed(2)}
                </div>
              </>
            ) : null}
          </div>
        </AppCollapse>
      ) : null}

      {order?.storeInfo?.baseLanguage?.name ? (
        <AppCollapse
          title={t("Store Information")}
          border={false}
        >
          <div className="relative grid grid-cols-2 gap-y-6">
            <div className="text-14px font-medium text-gray-700">
              {t("Store Name")}
            </div>
            <div className="text-14px text-right text-gray-500">
              {order.storeInfo.baseLanguage.name}
            </div>
          </div>
        </AppCollapse>
      ) : null}

      {order?.isPreOrder ? (
        <AppCollapse
          title={t("Pre Order Information")}
          border={false}
        >
          <div className="text-gray-500">
            {order?.orderType?.type === C.SERVICE_TYPES.DELIVERY
              ? t("Pre Order Delivery Slot")
              : t("Pre Order Pickup Slot")}
          </div>
          <div className="font-bold">
            <div>
              Date :{" "}
              {moment
                .unix(order?.preOrder?.preOrderSlotStartTime)
                .format("DD-MM-YYYY")}
            </div>
            Slot :{" "}
            {moment
              .unix(order?.preOrder?.preOrderSlotStartTime)
              .format("hh:mm A")}{" "}
            -{" "}
            {moment
              .unix(order?.preOrder?.preOrderSlotEndTime)
              .format("hh:mm A")}
          </div>
        </AppCollapse>
      ) : null}

      <RefundOrderInfo rowData={order} />

      {order?.orderLevelSpecialRequest?.trim()?.length > 0 ? (
        <AppCollapse
          title={t("Special Note")}
          border={false}
          defaultOpen={true}
        >
          <div className="text-14px text-gray-700">
            {order.orderLevelSpecialRequest}
          </div>
        </AppCollapse>
      ) : null}

      <AppCollapse
        title={t("Payment Details")}
        border={false}
      >
        <PaymentInfo
          orderInfo={order}
          {...{ paymentGatewayInfoModal, setPaymentGatewayInfoModal }}
        >
          <div className="text-gray-500">{t("Payment")}</div>

          <div
            className={`font-medium  pe-1 ${
              order?.paymentType === C.PAYMENT_TYPE_ONLINE &&
              UTILS.getORderPaymentGateway(order) === C.PAYMENT_GATWAYS.STRIPE
                ? "cursor-pointer underline text-blue-600 hover:text-blue-800 mb-1"
                : ""
            }`}
          >
            {order.selectedPaymentMethod
              ? currentLanguage === "EN"
                ? order?.selectedPaymentMethod?.EN?.name
                : order?.selectedPaymentMethod?.AR?.name
              : "..."}
          </div>
        </PaymentInfo>
      </AppCollapse>

      <AppCollapse
        title={t("Items & totals")}
        border={false}
        defaultOpen={true}
      >
        <div className="-mx-2">
          <TableItemsDetail order={order} />
        </div>
      </AppCollapse>
    </>
  );
};

export default OrderDetailPreview;
